import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ProductTypeViewModel, ProductTypeCreateModel, ProductTypeUpdateModel } from '../types/product-type';
import { apiClient } from '../client/client';
import type { SpecConstraintViewModel } from '../types/product-type/SpecConstraintViewModel';

export interface ProductTypeQuery {
  secondaryCategoryId?: number | null;
  isDeleted?: boolean;
  pagination?: { pageSize?: number; pageNumber?: number };
}

export class ProductTypeService {
  private static readonly BASE = '/api/product-type';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getAll(query?: ProductTypeQuery): Promise<ServiceResultT<ProductTypeViewModel[]>> {
    const params: Record<string, number | boolean> = {};
    if (query?.secondaryCategoryId != null) params.SecondaryCategoryId = query.secondaryCategoryId;
    if (query?.isDeleted != null) params.IsDeleted = query.isDeleted;
    if (query?.pagination?.pageSize != null) params.PageSize = query.pagination.pageSize;
    if (query?.pagination?.pageNumber != null) params.PageNumber = query.pagination.pageNumber;
    const { data } = await this.client.get<ServiceResultT<ProductTypeViewModel[]>>(
      ProductTypeService.BASE,
      { params: Object.keys(params).length ? params : undefined }
    );
    return data;
  }

  async getById(id: number | string): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.get<ServiceResultT<ProductTypeViewModel>>(
      `${ProductTypeService.BASE}/${id}`
    );
    return data;
  }

  async create(payload: ProductTypeCreateModel): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ProductTypeViewModel>>(
      ProductTypeService.BASE,
      payload
    );
    return data;
  }

  async update(id: number | string, payload: ProductTypeUpdateModel): Promise<ServiceResultT<ProductTypeViewModel>> {
    const { data } = await this.client.put<ServiceResultT<ProductTypeViewModel>>(
      `${ProductTypeService.BASE}/${id}`,
      payload
    );
    return data;
  }

  async delete(id: number | string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(`${ProductTypeService.BASE}/${id}`);
    return data;
  }

  async getSpecConstraints(productTypeId: number): Promise<ServiceResultT<SpecConstraintViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<SpecConstraintViewModel[]>>(
      `${ProductTypeService.BASE}/${productTypeId}/constraints`
    );
    return data;
  }
}

export const productTypeService = new ProductTypeService();
